/**
 * Device pricing configuration grid — per-account pricing (default
 * price + site group/site overrides) for each billable device
 * category, gated by the account's virtual-billing and inventory-
 * policy agreement terms.
 */
import { FormControl, MenuItem, Select, TextField, Tooltip, Typography } from '@mui/material';
import React from 'react';

import * as BillingApi from '@sophocles/api/billing';
import { loadDevicePricingConfig, saveDevicePricingConfig } from '@sophocles/features/billing/api';
import {
  buildActionsColumn,
  buildOverridesColumn,
  EnumCellDisplay,
} from '@sophocles/views/billing/components/pricingConfigColumns';
import PricingConfigGrid from '@sophocles/views/billing/components/PricingConfigGrid';

function getPricingUnits(t) {
  return [
    { value: 'FLAT', label: t('billing.pricingConfig.pricingUnitFlat') },
    { value: 'PER_SITE_GROUP', label: t('billing.pricingConfig.pricingUnitPerSiteGroup') },
    { value: 'PER_SITE', label: t('billing.pricingConfig.pricingUnitPerSite') },
    { value: 'PER_DEVICE', label: t('billing.pricingConfig.pricingUnitPerDevice') },
  ];
}

// Virtual/inventory billing terms can disable a row entirely — feature
// pricing rows have no equivalent gate.
function getRowDisabledState(row, { t, isBilledVirtual, inventoryPolicy }) {
  const isVirtualCategory = row.category?.is_virtual === true;
  const isInventoryCategory = row.category?.is_inventory === true;

  if (isVirtualCategory && !isBilledVirtual) {
    return { disabled: true, tooltip: t('billing.pricingConfig.virtualBillingDisabledTooltip') };
  }
  if (isInventoryCategory && inventoryPolicy === 'none') {
    return { disabled: true, tooltip: t('billing.pricingConfig.inventoryBillingDisabledTooltip') };
  }
  return { disabled: false, tooltip: null };
}

const deviceDescriptor = {
  configType: 'device',
  getRowKey: config => config.category?.id,
  entityLabel: config => config.category?.name,
  fetchDefaultTemplate: () => BillingApi.fetchDefaultDevicePricingTemplate(),
  loadTemplateFailedLogKey: 'billing.pricingConfig.loadDeviceTemplateFailedLog',
  loadTemplateFailedMessageKey: 'billing.pricingConfig.loadDeviceTemplateFailed',
  loadAction: loadDevicePricingConfig,
  saveAction: saveDevicePricingConfig,
  buildSaveConfigEntry: c => ({
    category_id: c.category?.id,
    pricing_unit: c.pricing_unit,
    default_price: c.default_price,
    overrides: (c.overrides || []).map(o => ({
      site_group_id: o.site_group?.id || null,
      site_id: o.site?.id || null,
      price: o.price,
    })),
  }),
  gridKey: ({ isBilledVirtual, inventoryPolicy }) => `grid-${isBilledVirtual}-${inventoryPolicy}`,
  buildColumns: ({
    t,
    editMode,
    alwaysEditMode,
    onUpdateConfig,
    onOpenOverrides,
    onActionMenuOpen,
    isBilledVirtual,
    inventoryPolicy,
  }) => {
    const disabledState = row => getRowDisabledState(row, { t, isBilledVirtual, inventoryPolicy });
    const pricingUnits = getPricingUnits(t);

    return [
      {
        field: 'categoryName',
        headerName: t('billing.pricingConfig.columnCategory'),
        flex: 1,
        minWidth: 180,
        valueGetter: (value, row) => row.category?.name || '',
        renderCell: params => {
          const { disabled, tooltip } = disabledState(params.row);
          const content = <EnumCellDisplay label={params.row.category?.name || ''} disabled={disabled} />;
          if (disabled && tooltip) {
            return (
              <Tooltip title={tooltip} arrow>
                {content}
              </Tooltip>
            );
          }
          return content;
        },
      },
      {
        field: 'pricing_unit',
        headerName: t('billing.pricingConfig.columnPricingUnit'),
        width: 180,
        renderCell: params => {
          // Default to PER_DEVICE when not configured (matches billing engine behavior)
          const displayValue = params.value || 'PER_DEVICE';
          const { disabled: isRowDisabled, tooltip } = disabledState(params.row);

          if (editMode) {
            const selectField = (
              <FormControl
                size="small"
                fullWidth
                hiddenLabel
                disabled={isRowDisabled}
                sx={{ height: '100%', justifyContent: 'center' }}
              >
                <Select
                  value={displayValue}
                  onChange={e =>
                    onUpdateConfig(params.row.category?.id, {
                      pricing_unit: e.target.value || null,
                      ...(e.target.value ? {} : { default_price: null }),
                      ...(e.target.value === 'FLAT' ? { overrides: [] } : {}),
                    })
                  }
                  onClick={e => e.stopPropagation()}
                  disabled={isRowDisabled}
                >
                  {pricingUnits.map(unit => (
                    <MenuItem key={unit.value} value={unit.value}>
                      {unit.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            );

            if (isRowDisabled && tooltip) {
              return (
                <Tooltip title={tooltip} arrow>
                  {selectField}
                </Tooltip>
              );
            }
            return selectField;
          }

          return (
            <EnumCellDisplay label={pricingUnits.find(u => u.value === displayValue)?.label} disabled={isRowDisabled} />
          );
        },
      },
      {
        field: 'default_price',
        headerName: t('billing.pricingConfig.columnDefaultPrice'),
        width: 140,
        renderCell: params => {
          const { disabled: isRowDisabled, tooltip } = disabledState(params.row);

          if (editMode) {
            const priceField = (
              <TextField
                size="small"
                hiddenLabel
                type="number"
                value={params.value || ''}
                onChange={e => onUpdateConfig(params.row.category?.id, { default_price: e.target.value || '0' })}
                onClick={e => e.stopPropagation()}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography>,
                }}
                inputProps={{ step: '0.01', min: '0' }}
                disabled={isRowDisabled}
                sx={{ width: '100%' }}
              />
            );

            if (isRowDisabled && tooltip) {
              return (
                <Tooltip title={tooltip} arrow>
                  {priceField}
                </Tooltip>
              );
            }
            return priceField;
          }

          return (
            <EnumCellDisplay
              label={
                params.value ? (
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500 }}
                    color={isRowDisabled ? 'text.disabled' : 'text.primary'}
                  >
                    ${parseFloat(params.value).toFixed(2)}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.disabled">
                    —
                  </Typography>
                )
              }
            />
          );
        },
      },
      // Overrides column - hidden in alwaysEditMode (inline forms don't support overrides)
      ...(!alwaysEditMode
        ? [
            buildOverridesColumn({
              t,
              onOpenOverrides,
              getOverridesGate: row => {
                const pricingUnit = row.pricing_unit || 'PER_DEVICE';
                const { disabled: isRowDisabled, tooltip } = disabledState(row);
                if (pricingUnit === 'FLAT') {
                  return { na: true, tooltip: null };
                }
                return { disabled: pricingUnit === 'FLAT' || isRowDisabled, tooltip };
              },
            }),
          ]
        : []),
      // Actions column - only visible in edit mode and not in alwaysEditMode (inline forms)
      ...(editMode && !alwaysEditMode
        ? [buildActionsColumn({ onActionMenuOpen, isRowDisabled: row => disabledState(row).disabled })]
        : []),
    ];
  },
};

/**
 * DevicePricingConfigGrid - Grid for configuring device pricing by category
 *
 * Supports two modes:
 * 1. Redux mode (default): Pass accountId to load/save configs via Redux/API
 * 2. Controlled mode: Pass value/onChange to manage configs locally (for inline forms)
 *
 * @param {string} accountId - The billing account ID (required for Redux mode)
 * @param {string} customerId - Customer ID for autocompletes
 * @param {function} renderToolbar - Custom toolbar renderer
 * @param {boolean} isBilledVirtual - Whether virtual billing is enabled
 * @param {string} inventoryPolicy - Inventory policy
 * @param {Array} value - Controlled mode: array of config objects
 * @param {function} onChange - Controlled mode: callback when configs change
 * @param {boolean} alwaysEditMode - Always be in edit mode (for inline forms)
 */
function DevicePricingConfigGrid({ isBilledVirtual = false, inventoryPolicy = 'exact', ...props }) {
  return (
    <PricingConfigGrid
      descriptor={deviceDescriptor}
      isBilledVirtual={isBilledVirtual}
      inventoryPolicy={inventoryPolicy}
      {...props}
    />
  );
}

export default DevicePricingConfigGrid;
